import React, { useContext, useEffect, useState } from 'react';
import {
  AnsweringSubscriptionJsonType,
  AnsweringSubscriptionLevel,
} from '@bb/domain/dist/types';
import type {
  GetPortalAnsweringConfigResponse,
  AnsweringServicePayer,
  ISubscribeAnsweringServiceBody,
  AnsweringServiceBillingDetails,
} from '@/app-package';
import { AnsweringServiceType } from '@bb/domain/dist/types/AnsweringSubscription';
import { findActiveSubscription } from 'api/AnsweringSubscriptionController';
import {
  cancelAnsweringServiceSubscription,
  createAnsweringServiceSubscription,
  getProductBillingDetails,
} from 'api/AnsweringServiceController';
import { useAnsweringServiceDetails } from 'hooks';
import { getEnabledListingCountForAppointmentDesk } from 'api/ListingController';
import { AuthContext } from 'contexts';
import { message } from '@bb/elements-web';
import { getLocalTimeZone } from 'utils';
import { format } from 'date-fns-tz';
import usesLegacyServiceProvider from 'utils/usesLegacyAnsweringServiceProvider';
import useOverduePaymentStage from 'hooks/useOverduePaymentStage';
import { useHistory } from 'react-router-dom';

/** The four steps a user or brokerage goes through when
 *  signing up for a new Answering Service subscription. */
export enum AnsweringServiceSubscribeStep {
  LearnMore = 1,
  ManageListings = 2,
  PaymentConfirmation = 3,
  SubscriptionSuccess = 4,
}

// Define the shape for our context
export type AnsweringServiceContextType = {
  /** The user/brokerage's already active Answering Service subscription, if one exists. */
  answeringSubscription?: Omit<AnsweringSubscriptionJsonType, 'startDate'> & {
    startDate: Date;
  };
  /** A method that redirects the browser to whatever page the user can manage their billing at.
   *  Used at the end of the flow for creating a new subscription. */
  billingRedirection: () => void;
  /** The method to trigger the Answering Service subscription creation in the
   * backend and then move the user to the next screen in the flow afterwards. */
  createSubscription: (
    billingProfile: ISubscribeAnsweringServiceBody['billingProfile'],
  ) => void;
  /** The method to cancel the current Answering Service subscription. */
  cancelSubscription: () => Promise<boolean>;
  /** Which of the four steps of the Answering Service subscription process the user is on. */
  currentStep: number;
  /** The type of Answering Service this user currently actively has. */
  currentSubscriptionType: AnsweringServiceType | null;
  /** Whether this user's preference is to have Answering Service turned on for all new listings they receive by default. */
  defaultEnabled: boolean;
  /** The number of listings this user or brokerage has which have Answering Service enabled. */
  enabledListingsCount: number;
  /** Whether the user has an active Answering Service subscription or is currently unsubscribed. */
  hasActiveAnsweringService: boolean;
  /** The Onboard Portal configurations this user's MLS has specific to their Answering Service setup. */
  portalConfig?: GetPortalAnsweringConfigResponse;
  /** The method for setting the current step in the subscription flow.
   * Exported so that other pages can directly call it. */
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  /** The method for setting the user's preference for automatically turning on
   * Answering Service for new listings they receive by default.
   * Defined here so both this Context and the ListingTable can set it. */
  setDefaultEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  /** Whether the Subscribe btn should be unclickable and display loading because the backend is creating a new subscription. */
  subscribeBtnLoading?: boolean;
  /** Who pays for this Answering Service subscription. */
  subscriptionPayer: AnsweringServicePayer;
  /** The user viewing this page. */
  userId?: string;
  /** Whether it is a user or a brokerage going through this flow. */
  viewingLevel: AnsweringSubscriptionLevel;
  billingDetails?: AnsweringServiceBillingDetails | undefined;
  setBillingDetails: React.Dispatch<
    React.SetStateAction<AnsweringServiceBillingDetails | undefined>
  >;
  /** Whether the user/brokerage's current subscription is being fetched. */
  fetchingSubscription: boolean;
  /** Whether the billing details and legacy provider check are being fetched. */
  fetchingBillingInfo: boolean;
  /** Whether the legacy provider check is in progress. */
  loadingLegacyStatus: boolean;
};

// Define the props the provider takes in
interface AnsweringServiceContextProviderProps {
  /** Whether it is a user or a brokerage going through this flow. */
  viewingLevel: AnsweringSubscriptionLevel;
  /** A method that redirects the browser to whatever page the user can manage their billing at.
   *  Will be used at the end of a new subscription flow & with back buttons. */
  billingRedirection: () => void;
}

/** The Context for all values shared across multiple pages during the Answering Service subscription flow. */
const AnsweringServiceContext =
  React.createContext<AnsweringServiceContextType>(null!);

// Create the actual provider with all the values it will export
export const AnsweringServiceContextProvider: React.FC<
  AnsweringServiceContextProviderProps
> = ({ viewingLevel, billingRedirection, children }) => {
  const { answeringPortalConfig, auth, loadingAnsweringPortalConfig } =
    useContext(AuthContext);
  const history = useHistory();
  const orgId = auth.org?.id;
  const userId = auth.id;

  // Answering Service subscription
  const [answeringSubscription, setAnsweringSubscription] =
    useState<AnsweringServiceContextType['answeringSubscription']>();
  const [fetchingSubscription, setFetchingSubscription] = useState(true);

  // Frontend component states
  // (See definitions in the AnsweringServiceContextType above)
  const [currentSubscriptionType, setCurrentSubscriptionType] =
    useState<AnsweringServiceType>(null);
  const [currentStep, setCurrentStep] =
    useState<AnsweringServiceSubscribeStep>(1); // Default them to the first step in the process.
  const [defaultEnabled, setDefaultEnabled] = useState(true);
  const [enabledListingsCount, setEnabledListingsCount] = useState(0);
  const [hasActiveAnsweringService, setHasActiveAnsweringService] =
    useState(false);
  const [subscriptionPayer, setSubscriptionPayer] =
    useState<AnsweringServicePayer>(null);
  const [subscribeBtnLoading, setSubscribeBtnLoading] = useState(false);
  const [billingDetails, setBillingDetails] = useState<
    AnsweringServiceBillingDetails | undefined
  >();
  const [fetchingBillingInfo, setFetchingBillingInfo] = useState(false);
  const [loadingLegacyStatus, setLoadingLegacyStatus] = useState(true);
  const [hasLoadingError, setHasLoadingError] = useState(false);

  // Grab all the Answering Service values from the shared hook code,
  // then set them here to be re-exported.
  const {
    answeringServiceType: fetchedServiceType,
    answeringServicePayer: fetchedServicePayer,
    hasActiveAnsweringService: fetchedActiveStatus,
  } = useAnsweringServiceDetails({ viewingLevel, userId });

  useEffect(() => {
    setSubscriptionPayer(fetchedServicePayer);
    setCurrentSubscriptionType(fetchedServiceType);
    setHasActiveAnsweringService(fetchedActiveStatus);
  }, [fetchedServiceType, fetchedServicePayer, fetchedActiveStatus]);

  const { isUserSuspended, isOrgSuspended } = useOverduePaymentStage();

  // Under certain conditions, redirect the user accessing the LCS page to the Subscription Hub
  useEffect(() => {
    if (
      viewingLevel === 'user' &&
      subscriptionPayer === 'user' &&
      hasActiveAnsweringService &&
      isUserSuspended
    ) {
      history.push(`/subscriptions_payments/user`);
    }

    if (
      viewingLevel === 'org' &&
      subscriptionPayer === 'brokerage' &&
      hasActiveAnsweringService &&
      isOrgSuspended
    ) {
      history.push(`/subscriptions_payments/org`);
    }
  }, [
    history,
    viewingLevel,
    subscriptionPayer,
    hasActiveAnsweringService,
    isUserSuspended,
    isOrgSuspended,
  ]);

  useEffect(() => {
    // redirect if there was an error loading any necessary details to subscribe
    if (hasLoadingError) {
      billingRedirection();
    }
  }, [billingRedirection, hasLoadingError]);

  // To be used when calculating the total that the user/brokerage has to pay. (Currently unused.)
  useEffect(() => {
    /** Go grab the number of listings this agent/brokerage has with Answering Service activated. */
    const fetchEnabledCount = async () => {
      try {
        const enabled =
          await getEnabledListingCountForAppointmentDesk(viewingLevel);
        setEnabledListingsCount(enabled.count);
      } catch (err) {
        message.error(
          'Error fetching count of listings with Answering Service enabled',
        );
      }
    };

    // Only calculate when this is a user paying and they are on the PaymentConfirmation step
    if (
      viewingLevel === 'user' &&
      currentStep === AnsweringServiceSubscribeStep.PaymentConfirmation
    ) {
      fetchEnabledCount();
    }
  }, [currentStep, viewingLevel]);

  useEffect(() => {
    /** Method to fetch the user/brokerage's currently-existing subscription if they have one
     *  and push them to the proper step in the flow if they do. */
    const fetchSubscription = async () => {
      try {
        const subscriptionData = await findActiveSubscription({
          subscriptionLevel: viewingLevel,
          userId,
        });
        if (subscriptionData) {
          // If they have an active existing subscription, set the data
          setHasActiveAnsweringService(true);
          setAnsweringSubscription({
            ...subscriptionData,
            startDate: new Date(subscriptionData.startDate),
          });
          setDefaultEnabled(subscriptionData.defaultEnabled !== false);
          // Send them to the ManageListings step if they already have a sub
          setCurrentStep(AnsweringServiceSubscribeStep.ManageListings);
        }
      } catch (err) {
        message.error('Error fetching active subscription');
      }
      setFetchingSubscription(false);
    };

    fetchSubscription();
  }, [viewingLevel, orgId, userId, answeringPortalConfig]);

  useEffect(() => {
    let isMounted = true;

    const fetchBillingInfo = async () => {
      setFetchingBillingInfo(true);

      try {
        const userTimezone = getLocalTimeZone(auth);
        const startDate = format(new Date(), 'yyyy-MM-dd', {
          timeZone: userTimezone,
        });

        const fetchedBillingDetails = await getProductBillingDetails({
          serviceLevel: viewingLevel,
          startDate,
        });

        if (isMounted) {
          setBillingDetails(fetchedBillingDetails);
        }
      } catch (error) {
        if (isMounted) {
          message.error(
            'Failed to load billing details. Please refresh the page and try again.',
            10,
          );
        }

        setHasLoadingError(true);
      } finally {
        if (isMounted) {
          setFetchingBillingInfo(false);
        }
      }
    };

    const checkLegacyProvider = async () => {
      try {
        if (
          !loadingAnsweringPortalConfig &&
          (await usesLegacyServiceProvider(answeringPortalConfig, auth.org))
        ) {
          if (isMounted) {
            window.location.replace('#/admin/answering');
          }
        } else if (isMounted) {
          setLoadingLegacyStatus(false);
        }
      } catch (err) {
        if (isMounted) {
          message.error('Error checking answering service provider');
          setLoadingLegacyStatus(false);
        }
      }
    };

    fetchBillingInfo();
    checkLegacyProvider();

    return () => {
      isMounted = false;
    };
  }, [viewingLevel, auth, answeringPortalConfig, loadingAnsweringPortalConfig]);

  /**
   * The method to trigger the Answering Service subscription creation in the
   * backend and then move the user to the next screen in the flow afterwards.
   * */
  const createSubscription = async (
    billingProfile: ISubscribeAnsweringServiceBody['billingProfile'],
  ) => {
    setSubscribeBtnLoading(true);

    try {
      await createAnsweringServiceSubscription({
        subscriptionLevel: viewingLevel,
        billingProfile,
      });

      setCurrentStep(AnsweringServiceSubscribeStep.SubscriptionSuccess);
      setHasActiveAnsweringService(true);
    } catch (err) {
      message.error('Error subscribing to Live Concierge Service');
    }
    setSubscribeBtnLoading(false);
  };

  /**
   * Cancels the currently active Answering Service subscription.
   */
  const cancelSubscription = async (): Promise<boolean> => {
    if (!answeringSubscription?.id) {
      message.error('No active subscription found to cancel');
      return false;
    }

    setSubscribeBtnLoading(true);

    try {
      await cancelAnsweringServiceSubscription(answeringSubscription.id);
      message.success(
        'Live Concierge Service subscription cancelled successfully',
      );

      setAnsweringSubscription(undefined);
      setHasActiveAnsweringService(false);
      setSubscriptionPayer(null);
      setCurrentStep(AnsweringServiceSubscribeStep.LearnMore);
      return true;
    } catch (err) {
      message.error('Error cancelling Live Concierge Service');
      return false;
    } finally {
      setSubscribeBtnLoading(false);
    }
  };

  /** The values the context exports */
  return (
    <AnsweringServiceContext.Provider
      value={{
        answeringSubscription,
        billingRedirection,
        cancelSubscription,
        createSubscription,
        currentSubscriptionType,
        fetchingSubscription,
        fetchingBillingInfo,
        loadingLegacyStatus,
        currentStep,
        defaultEnabled,
        enabledListingsCount,
        hasActiveAnsweringService,
        portalConfig: answeringPortalConfig,
        setCurrentStep,
        setDefaultEnabled,
        subscribeBtnLoading,
        subscriptionPayer,
        userId,
        viewingLevel,
        billingDetails,
        setBillingDetails,
      }}
    >
      {children}
    </AnsweringServiceContext.Provider>
  );
};

export default AnsweringServiceContext;
